// src/components/auth/DetalleMutuoPermissionGuard.tsx

import React, { ReactNode } from 'react';
import { TipoFuncionalidad } from '../../utils/enums';
import { useUserRolesContext } from '../../context/userRolesContext';

interface DetalleMutuoPermissionGuardProps {
    children: ReactNode;
    /**
     * Indica si el mutuo está vigente.
     * - false = Origen Bandeja (mutuo no vigente)
     * - true = Origen Cartera (mutuo vigente)
     */
    esVigente?: boolean;
    /**
     * Permiso requerido.
     * 
     * El componente valida automáticamente que el permiso corresponda al origen:
     * - Si esVigente = false (Bandeja): solo acepta permisos de Bandeja
     * - Si esVigente = true (Cartera): solo acepta permisos de Cartera
     * 
     * Permisos de Bandeja: Bandeja_ver, Bandeja_ingresar, Activar_mutuo, Bandeja_editar, Bandeja_aprobar
     * Permisos de Cartera: Cartera_ver, Cartera_editar_info, Cartera_editar_contacto
     */
    permission?: TipoFuncionalidad;
    /**
     * Lista de permisos (al menos uno debe cumplirse)
     */
    permissions?: TipoFuncionalidad[];
    /**
     * Si true, requiere TODOS los permisos de la lista.
     * Si false, requiere AL MENOS UNO.
     * @default false
     */
    requireAllPermissions?: boolean;
    /**
     * Contenido a mostrar si no tiene acceso
     */
    fallback?: ReactNode;
}

// Permisos válidos por origen
const PERMISOS_BANDEJA = [
    TipoFuncionalidad.Bandeja_ver,
    TipoFuncionalidad.Bandeja_ingresar,
    TipoFuncionalidad.Activar_mutuo,
    TipoFuncionalidad.Bandeja_editar,
    TipoFuncionalidad.Bandeja_aprobar
];

const PERMISOS_CARTERA = [
    TipoFuncionalidad.Cartera_ver,
    TipoFuncionalidad.Cartera_editar_info,
    TipoFuncionalidad.Cartera_editar_contacto,
    TipoFuncionalidad.Desactivar_mutuo
    // TipoFuncionalidad.Cartera_editar
];

/**
 * Guard especializado para el módulo de Detalle Mutuo.
 * 
 * Valida automáticamente que:
 * 1. El permiso solicitado corresponda al origen actual (Bandeja/Cartera)
 * 2. El usuario tenga ese permiso
 * 
 * **Lógica automática:**
 * - Si esVigente = false (Bandeja): solo acepta permisos que empiecen con "Bandeja_"
 * - Si esVigente = true (Cartera): solo acepta permisos que empiecen con "Cartera_"
 * 
 * @example
 * // Mostrar botón solo si viene de Bandeja Y tiene permiso Bandeja_editar
 * <DetalleMutuoPermissionGuard
 *   esVigente={esVigente}
 *   permission={TipoFuncionalidad.Bandeja_editar}
 * >
 *   <EditButton />
 * </DetalleMutuoPermissionGuard>
 * 
 * @example
 * // Mostrar sección solo si viene de Cartera Y tiene permiso Cartera_editar_info
 * <DetalleMutuoPermissionGuard
 *   esVigente={esVigente}
 *   permission={TipoFuncionalidad.Cartera_editar_info}
 * >
 *   <CardSection />
 * </DetalleMutuoPermissionGuard>
 */
export const DetalleMutuoPermissionGuard: React.FC<DetalleMutuoPermissionGuardProps> = ({
    children,
    esVigente,
    permission,
    permissions,
    requireAllPermissions = false,
    fallback = null
}) => {
    const { hasPermission, hasAnyPermission, hasAllPermissions, isLoading, isAuthenticated } = useUserRolesContext();

    if (isLoading) {
        return null;
    }

    if (!isAuthenticated) {
        return <>{fallback}</>;
    }

    // Determinar origen actual basado en esVigente
    const origenResuelto = esVigente !== undefined && esVigente !== null;
    const esBandeja = origenResuelto ? !esVigente : false;

    // Función para validar si un permiso corresponde al origen actual
    const permisoCorrespondeAlOrigen = (perm: TipoFuncionalidad): boolean => {
        if (!origenResuelto) {
            // Mientras el origen no se resuelve, no filtrar por origen para evitar parpadeo y desmontes.
            return true;
        }

        if (esBandeja) {
            return PERMISOS_BANDEJA.includes(perm);
        } else {
            return PERMISOS_CARTERA.includes(perm);
        }
    };

    // Validar permisos
    let tieneAcceso = false;

    if (permission) {
        // Validar un solo permiso
        // 1. Verificar que el permiso corresponda al origen
        if (!permisoCorrespondeAlOrigen(permission)) {
            return <>{fallback}</>;
        }
        // 2. Verificar que el usuario tenga el permiso
        tieneAcceso = hasPermission(permission);
    } else if (permissions && permissions.length > 0) {
        // Validar múltiples permisos
        // 1. Filtrar solo los permisos que correspondan al origen actual
        const permisosValidos = permissions.filter(permisoCorrespondeAlOrigen);

        if (permisosValidos.length === 0) {
            return <>{fallback}</>;
        }

        // 2. Verificar que el usuario tenga los permisos
        if (requireAllPermissions) {
            tieneAcceso = hasAllPermissions(permisosValidos);
        } else {
            tieneAcceso = hasAnyPermission(permisosValidos);
        }
    } else {
        // Si no se especificó ningún permiso, permitir acceso
        tieneAcceso = true;
    }

    return tieneAcceso ? <>{children}</> : <>{fallback}</>;
};

/**
 * Hook para verificar acceso desde componentes
 */
export const useDetalleMutuoAccess = (
    esVigente?: boolean,
    permission?: TipoFuncionalidad,
    permissions?: TipoFuncionalidad[],
    requireAllPermissions: boolean = false
) => {
    const { hasPermission, hasAnyPermission, hasAllPermissions } = useUserRolesContext();

    const esBandeja = !esVigente;
    const esCartera = !!esVigente;

    const permisoCorrespondeAlOrigen = (perm: TipoFuncionalidad): boolean => {
        if (esBandeja) {
            return PERMISOS_BANDEJA.includes(perm);
        } else {
            return PERMISOS_CARTERA.includes(perm);
        }
    };

    let tieneAcceso = false;
    let permisoValido = false;

    if (permission) {
        permisoValido = permisoCorrespondeAlOrigen(permission);
        if (permisoValido) {
            tieneAcceso = hasPermission(permission);
        }
    } else if (permissions && permissions.length > 0) {
        const permisosValidos = permissions.filter(permisoCorrespondeAlOrigen);
        permisoValido = permisosValidos.length > 0;

        if (permisoValido) {
            if (requireAllPermissions) {
                tieneAcceso = hasAllPermissions(permisosValidos);
            } else {
                tieneAcceso = hasAnyPermission(permisosValidos);
            }
        }
    } else {
        permisoValido = true;
        tieneAcceso = true;
    }

    return {
        hasAccess: tieneAcceso,
        esBandeja,
        esCartera,
        permisoValido
    };
};